import {
    PackageStateResult,
    ProjectResult,
    DefaultProjectRes,
    PackageSetItemModel,
    SearchPackagesRestResponse,
    SearchPackagesRequest,
    SearchPackageLinesRequest,
    SearchPackageLinesRestResponse
} from './types';
import security from './SecurityService';
import appClient from './AppClient';
import { endpoints } from '../../../config';

type PackageSetItemRestModel = {
    id: string;
    fileName: string;
    state: PackageStateResult;
    contentType: string;
    fileSizeBytes: number;
    indexDate: string;
};

export default class ProjectsService {
    async getProjects(): Promise<ProjectResult[]> {
        const result = await appClient.get<ProjectResult[]>(endpoints.manageUrl + 'projects');

        if (result.isErr()) {
            return [];
        }

        return result.value || [];
    }

    async searchPackages({
        projectIds = [],
        page = 0,
        pageSize = 50,
        searchTerm = null,
        fieldsSearch = null,
        allSources = true,
        orderBy = null,
        distinctBy = null,
        uploadedBy = null
    }: SearchPackagesRequest) {
        const result = await appClient.post<SearchPackagesRestResponse>(endpoints.manageUrl + 'packages/search', {
            projectIds,
            searchTerm,
            pageSize,
            page,
            fieldsSearch,
            allSources,
            orderBy,
            distinctBy,
            uploadedBy
        });

        if (result.isErr()) {
            return { packages: [], total: 0 };
        }

        return result.value;
    }

    async searchPackagesForAutocomplete({
        projectIds = [],
        page = 0,
        pageSize = 15,
        searchTerm = null,
        fieldsSearch = null,
        allSources = true,
        orderBy = null,
        distinctBy = null,
        uploadedBy = null
    }: SearchPackagesRequest) {
        const result = await appClient.post<SearchPackagesRestResponse>(endpoints.manageUrl + 'packages/search', {
            projectIds,
            searchTerm,
            pageSize,
            page,
            fieldsSearch,
            allSources,
            orderBy,
            distinctBy,
            uploadedBy
        });

        if (result.isErr()) {
            return { packages: [], total: 0 };
        }

        return result.value;
    }

    async getDefaultProject(): Promise<DefaultProjectRes | null> {
        const result = await appClient.get<DefaultProjectRes | null>(endpoints.manageUrl + 'projects/default-project');

        if (result.isErr()) {
            return null;
        }

        return result.value;
    }

    // eslint-disable-next-line max-len
    async getPackages(
        projectId: string,
        state: PackageStateResult = PackageStateResult.Ready
    ): Promise<ProjectResult | null> {
        const url =
            endpoints.manageUrl +
            `projects/${encodeURIComponent(projectId)}/packages?state=${encodeURIComponent(state)}`;
        const result = await appClient.get<ProjectResult | null>(url);

        if (result.isErr() || !result.value) {
            return null;
        }

        return result.value;
    }

    handleDownload(id: string, fileType: 'pdf' | 'apkg') {
        let path = encodeURIComponent(id);
        security.invoke(token => {
            let t = '?access_token=' + encodeURIComponent(token);
            let url = '';
            if (fileType === 'pdf') {
                url = `${endpoints.manageUrl}document/${path}${t}`;
            } else {
                url = `${endpoints.manageUrl}file/${fileType}/${path}${t}`;
            }
            window.open(url);
            return Promise.resolve();
        });
    }

    getPackageAsync(packageId: string) {
        const url = endpoints.manageUrl + `packages/${packageId}?includeFields=false`;
        return appClient.get(url);
    }

    uploadPackage(formData: FormData, projectId: string) {
        const url = endpoints.manageUrl + `projects/${projectId}/iota/upload`;
        return appClient.post(url, formData);
    }

    uploadPackageSet(formData: FormData, projectId: string) {
        const url = endpoints.manageUrl + `projects/${projectId}/upload-set`;
        return appClient.post(url, formData);
    }

    getUserDisplayInfos(userIds: string[]) {
        const url = endpoints.usersUrl + 'display-users';
        return appClient.post<Array<{ id: string; userName: string; firstName?: string; lastName?: string }>>(url, {
            userIds
        });
    }

    async sendPackageViaEmail(id: string, emails: string[], message?: string) {
        var request = {
            emails,
            message
        };

        const url = endpoints.manageUrl + `packages/${id}/send-by-email`;
        return appClient.post(url, request);
    }

    async getPackageSet(projectId: string, packageSetId: string): Promise<PackageSetItemModel[]> {
        const url =
            endpoints.manageUrl +
            `projects/${encodeURIComponent(projectId)}/package-set/${encodeURIComponent(packageSetId)}/packages`;
        const result = await appClient.get<PackageSetItemRestModel[]>(url);

        if (result.isErr()) {
            return [];
        }

        return (result.value || []).map(pkg => ({
            id: pkg.id,
            fileName: pkg.fileName,
            contentType: pkg.contentType,
            state: pkg.state,
            fileSizeBytes: pkg.fileSizeBytes,
            indexDate: pkg.indexDate
        }));
    }

    async getPackageLines(request: SearchPackageLinesRequest) {
        const { projectId, packageId, searchTerm, pageSize, page, packageState, fieldsSearch, searchOptions } =
            request;

        const result = await appClient.post<SearchPackageLinesRestResponse>(
            endpoints.manageUrl + 'packages/search-lines',
            {
                projectId,
                packageId,
                searchTerm: searchTerm ?? null,
                pageSize,
                page: page ?? 0,
                packageState: packageState ?? PackageStateResult.Ready,
                fieldsSearch: fieldsSearch ?? null,
                searchOptions: searchOptions ?? null
            }
        );

        if (result.isErr()) {
            return null;
        }

        return result.value;
    }
}
